import React, { ComponentType } from 'react';
import { SubApp, SubAppConfig } from '../types/app.types';
import { ResourcesPage } from '../pages/ResourcesPage';
import EasyFlexPage from '../pages/EasyFlexPage';
import ChatDashboardPage from '../pages/ChatDashboardPage';
import ProfessionQuestions from '../pages/questions/ProfessionQuestions';
import ReferralLinksPage from '../pages/ReferralLinksPage';
import CompanyRequestsList from '../pages/company-requests/CompanyRequestsList';
import CertReviewQueuePage from '../pages/CertReviewQueuePage';
import DocumentAssessmentPage from '../pages/DocumentAssessmentPage';
import RecruitmentReportingPage from '../pages/RecruitmentReportingPage';
import PeopleOutlinedIcon from '@mui/icons-material/PeopleOutlined';
import ChatBubbleOutlinedIcon from '@mui/icons-material/ChatBubbleOutlined';
import QuizOutlinedIcon from '@mui/icons-material/QuizOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import VerifiedOutlinedIcon from '@mui/icons-material/VerifiedOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import SyncAltOutlinedIcon from '@mui/icons-material/SyncAltOutlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import TroubleshootOutlinedIcon from '@mui/icons-material/TroubleshootOutlined';
import { AI_OBSERVABILITY_ACCESS, WORKER_CHAT_ACCESS } from '../features/aiObservability/aiObservabilityAccess';
import { CHAT_DASHBOARD_ACCESS } from '../features/chat/chatDashboardAccess';
import { RECRUITMENT_MANAGE_WORKERS_ROLE } from '../constants/authRoles';
import { userRolesHaveRequiredRole } from '../utils/rolePermissions';

export const subAppConfigs: SubAppConfig[] = [
    {
        id: 'REFERRAL_LINKS' as SubApp,
        label: 'Referral Links',
        icon: React.createElement(LinkOutlinedIcon),
        menuItems: [
            { id: 'referral-links', label: 'Referral Links', path: '/referral-links' },
        ],
        component: ReferralLinksPage as ComponentType,
    },
    {
        id: SubApp.RESOURCES,
        label: 'Resources',
        icon: React.createElement(PeopleOutlinedIcon),
        menuItems: [
            { id: 'dashboard', label: 'Dashboard', path: '/resources' },
            { id: 'workers', label: 'Workers', path: '/resources/workers' },
            { id: 'bulk-import', label: 'Bulk import', path: '/resources/workers/bulk-import' },
            { id: 'pending-sessions', label: 'Pending sessions', path: '/resources/workers/pending-sessions' },
            { id: 'clients', label: 'Clients', path: '/resources/clients' },
            { id: 'sessions', label: 'Sessions', path: '/resources/sessions' },
        ],
        component: ResourcesPage as ComponentType,
    },
    {
        id: 'EASYFLEX' as SubApp,
        label: 'EasyFlex',
        icon: React.createElement(SyncAltOutlinedIcon),
        menuItems: [
            { id: 'easyflex', label: 'EasyFlex', path: '/resources/easyflex' },
        ],
        component: EasyFlexPage as ComponentType,
        featureFlag: 'easyflexPage',
    },
    {
        id: 'CERT_REVIEW' as SubApp,
        label: 'Certificate Review',
        icon: React.createElement(VerifiedOutlinedIcon),
        menuItems: [
            { id: 'cert-review', label: 'Certificate Review', path: '/cert-review' },
        ],
        component: CertReviewQueuePage as ComponentType,
    },
    {
        id: 'DOC_ASSESSMENT' as SubApp,
        label: 'Document Assessment',
        icon: React.createElement(FactCheckOutlinedIcon),
        menuItems: [
            { id: 'document-assessment', label: 'Document Assessment', path: '/document-assessment' },
        ],
        component: DocumentAssessmentPage as ComponentType,
    },
    {
        id: 'COMPANY_REQUESTS' as SubApp,
        label: 'Company Requests',
        icon: React.createElement(BusinessCenterOutlinedIcon),
        menuItems: [
            { id: 'company-requests', label: 'Company Requests', path: '/company-requests' },
        ],
        component: CompanyRequestsList as ComponentType,
    },
    // {
    //     id: SubApp.RECRUITMENT,
    //     label: 'Recruitment',
    //     menuItems: [
    //         { id: 'dashboard', label: 'Dashboard', path: '/recruitment' },
    //         { id: 'candidates', label: 'Candidates', path: '/recruitment/candidates' },
    //         { id: 'jobs', label: 'Jobs', path: '/recruitment/jobs' },
    //         { id: 'interviews', label: 'Interviews', path: '/recruitment/interviews' },
    //     ],
    //     component: RecruitmentPage,
    // },
    // Admin section - accessible only through role-based routes
    // {
    //     id: SubApp.ADMIN,
    //     label: 'Admin',
    //     menuItems: [
    //         // { id: 'dashboard', label: 'Dashboard', path: '/admin' },
    //         { id: 'users', label: 'Users', path: '/admin/users' },
    //         { id: 'debug', label: 'Debug Queue', path: '/admin/debug' },
    //         // { id: 'roles', label: 'Roles', path: '/admin/roles' },
    //         // { id: 'settings', label: 'Settings', path: '/admin/settings' },
    //     ],
    //     component: AdminApp,
    // },
    {
        id: SubApp.RECRUITMENT_REPORTING,
        label: 'Reporting',
        icon: React.createElement(AssessmentOutlinedIcon),
        menuItems: [
            { id: 'recruitment-reporting', label: 'Recruitment reporting', path: '/reporting/recruitment' },
        ],
        component: RecruitmentReportingPage as ComponentType,
        requiredRoles: [RECRUITMENT_MANAGE_WORKERS_ROLE],
    },
    {
        id: SubApp.AI_OBSERVABILITY,
        label: 'AI Observability',
        icon: React.createElement(TroubleshootOutlinedIcon),
        menuItems: [
            { id: 'ai-observability', label: 'AI Observability', path: '/admin/ai-observability' },
        ],
        component: React.lazy(() => import('../pages/AiDebugPage')),
        requiredAccess: AI_OBSERVABILITY_ACCESS,
    },
    {
        id: SubApp.WORKER_CHAT,
        label: 'Test worker chat',
        icon: React.createElement(ChatBubbleOutlinedIcon),
        menuItems: [{ id: 'worker-chat', label: 'Test worker chat', path: '/admin/worker-chat' }],
        component: React.lazy(() => import('../pages/WorkerChatTestPage')),
        requiredAccess: WORKER_CHAT_ACCESS,
    },
    {
        id: SubApp.CHAT,
        label: 'Whatsapp Chat',
        icon: React.createElement(ChatBubbleOutlinedIcon),
        menuItems: [
            { id: 'dashboard', label: 'Dashboard', path: '/chat' },
        ],
        component: ChatDashboardPage,
        requiredAccess: CHAT_DASHBOARD_ACCESS,
    },
    {
        id: SubApp.QUESTIONS,
        label: 'Questions',
        icon: React.createElement(QuizOutlinedIcon),
        menuItems: [
            { id: 'professions', label: 'Professions', path: '/questions' },
        ],
        component: ProfessionQuestions,
    },
];

export function subAppIsVisibleToRoles(app: SubAppConfig, roles: readonly (string | undefined)[]): boolean {
    return userRolesHaveRequiredRole(roles, app.requiredRoles);
}
